import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import { reportError } from 'src/common/services/posthogService';

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error | null, reset: () => void) => ReactNode);
  title?: string;
  message?: string;
  onReset?: () => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

export class ErrorBoundaryV2 extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundaryV2 caught a component crash:', error, errorInfo);
    reportError(error, {
      boundary: 'ErrorBoundaryV2',
      componentStack: errorInfo.componentStack
    });
  }

  handleReset = () => {
    if (this.props.onReset) {
      try {
        this.props.onReset();
      } catch (e) {
        console.error('Error resetting boundary:', e);
      }
    }
    this.setState({
      hasError: false,
      error: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback, title, message } = this.props;

    if (hasError) {
      // Dynamic fallback: can be a react element or a render function
      if (fallback) {
        if (typeof fallback === 'function') {
          return fallback(error, this.handleReset);
        }
        return fallback;
      }

      const displayTitle = title || 'Something went wrong';
      const displayMessage = message || 'We encountered an unexpected error while loading this section. Please try again or contact support if the issue persists.';

      return (
        <div className="flex-1 py-12 flex items-center justify-center w-full h-[calc(100vh-84px)] box-border transition-all duration-300">
          {/* Modern Glow Card */}
          <div className="relative w-full max-w-[550px] p-6 rounded-2xl border border-[var(--color-border-card,#E4E7EC)] bg-[var(--color-bg-card,#FFFFFF)] shadow-[0px_10px_30px_-5px_rgba(18,2,66,0.06)] text-center overflow-hidden">
            
            {/* Top decorative gradient bar */}
            <div className="absolute top-0 left-0 right-0 h-[4px] bg-gradient-to-r from-[var(--color-error-red,#F04438)] to-[var(--color-warning-orange2,#FD853A)]" />

            {/* Modern Pulse Warning Badge */}
            <div className="relative inline-flex items-center justify-center mb-6">
              {/* Outer Pulsing Glow */}
              <span className="absolute inline-flex h-20 w-20 rounded-full bg-[var(--color-error-pale,#FEF3F2)] opacity-60 filter blur-md animate-pulse pointer-events-none" />
              {/* Inner Icon Circle */}
              <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-tr from-[var(--color-error-red,#F04438)] to-[var(--color-error-light,#F98A8A)] text-white shadow-lg shadow-red-500/20">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="font-bold text-[var(--color-text-primary,#120242)] font-sans text-2xl mb-3 tracking-tight">
              {displayTitle}
            </h3>

            {/* Message */}
            <p className="text-[var(--color-text-secondary,#5D5F6C)] font-sans text-sm sm:text-base leading-relaxed max-w-[420px] mx-auto mb-8 opacity-90">
              {displayMessage}
            </p>

            {/* Action Buttons using MUI */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full mx-auto">
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleReload}
                sx={{
                  flex: 1,
                  width: '100%',
                  backgroundColor: 'var(--color-primary, #120242)',
                  color: 'var(--color-white, #FFFFFF)',
                  fontWeight: 600,
                  textTransform: 'none',
                  padding: '10px 24px',
                  borderRadius: '12px',
                  fontFamily: 'Inter, sans-serif',
                  boxShadow: '0px 4px 10px rgba(18, 2, 66, 0.15)',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Reload Page
              </Button>

              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={this.handleGoHome}
                sx={{
                  flex: 1,
                  width: '100%',
                  borderColor: 'var(--color-border, #D0D5DD)',
                  color: 'var(--color-text-dark, #344054)',
                  fontWeight: 600,
                  textTransform: 'none',
                  padding: '10px 24px',
                  borderRadius: '12px',
                  fontFamily: 'Inter, sans-serif',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}
